import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Wand2 } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareButtons } from "@/components/ShareButtons";

export default function SharedCreation() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/share/${id}`).then((r) => {
      setG(r.data);
      document.title = `${r.data.pet_name} as ${r.data.prompt_title} | Cutie Parlor`;
    }).catch((e) => setError(formatError(e)));
  }, [id, user]);

  const like = async () => {
    if (!user) return nav("/signup");
    const { data } = await api.post(`/community/${id}/like`);
    setG({ ...g, ...data });
    if (data.liked) toast.success("Liked!");
  };

  if (error) return (
    <main className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="shared-not-found">
      <p className="font-display text-3xl font-bold">Hmm, no cuties here.</p>
      <p className="mt-2 text-[#6E687A]">{error}</p>
      <Link to="/community" className="mt-6 inline-block font-bold text-[#FF5A79]">Browse the community</Link>
    </main>
  );
  if (!g) return <div className="max-w-3xl mx-auto px-4 py-16"><div className="shimmer aspect-square rounded-3xl" /></div>;

  const text = `Meet ${g.pet_name} as ${g.prompt_title}! Made with Cutie Parlor`;
  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-10 items-start" data-testid="shared-creation-page">
      <div className="lg:col-span-7 rounded-[2rem] bg-white sticker p-3 rise">
        <img src={fileUrl(g.output_path)} alt={text} className="w-full rounded-3xl aspect-square object-cover" data-testid="shared-image" />
      </div>
      <div className="lg:col-span-5 space-y-5">
        <p className="text-xs font-bold uppercase tracking-wider text-[#8A4FFF]">{g.prompt_title}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="shared-title">{g.pet_name} <span className="text-[#FF5A79]">served looks.</span></h1>
        <p className="text-[#6E687A]">Created by {g.author}</p>
        <button onClick={like} data-testid="shared-like-button"
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-bold sticker sticker-press ${g.liked ? "bg-[#FF5A79] text-white" : "bg-white"}`}>
          <Heart className={`h-4 w-4 ${g.liked ? "fill-current" : ""}`} /> {g.likes} {g.likes === 1 ? "like" : "likes"}
        </button>
        <ShareButtons id={id} text={text} />
        <div className="rounded-3xl bg-[#FFC72C] sticker p-6">
          <h2 className="text-2xl font-bold">Your pet could be next</h2>
          <p className="mt-1 text-sm">5 free AI creations every day. No talent required, only treats.</p>
          <Link to={user ? `/studio?prompt=${g.prompt_id}` : "/signup"} data-testid="shared-try-button"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1E1B26] text-white px-5 py-2.5 font-bold text-sm hover:bg-[#FF5A79] transition-colors">
            <Wand2 className="h-4 w-4" /> Try {g.prompt_title}
          </Link>
        </div>
      </div>
    </main>
  );
}
